import { QueryClient } from '@tanstack/react-query';

/**
 * Creates the QueryClient for one request on the server or one tab in the browser. It keeps the
 * behaviour of the Redux cache it replaces, and tuning freshness, retention or refresh triggers is a
 * separate decision for after the migration.
 *
 * - Data is never fresh (`staleTime` stays 0): a route loader built on `loadQuery` fetches on every
 *   navigation. Nothing is skipped through a freshness window, which would also skip a quick return
 *   to the same page.
 * - `refetchOnMount` is off, so a component that mounts over existing data does not fetch again.
 *   That covers the first client render over server data and a mount right after its loader ran. A
 *   query that has no data yet, such as one only the client fetches, still loads on mount.
 * - Inactive data is dropped after TanStack's default `gcTime`: 5 minutes after its last component
 *   unmounts in the browser, never on the server, whose cache lasts one request. Loaders fetch on
 *   every navigation anyway, so keeping it longer would only hold memory.
 * - The api-client already retries failed requests, so queries don't retry on top of it.
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
